import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * The QA report by email (0133, owner 26 September 2026): weekly or monthly, as a PDF, to people
 * picked from the account's own users -- never typed addresses.
 */

interface Schedule {
  id: string; frequency: 'weekly' | 'monthly'; group_by: 'agent' | 'department' | 'queue' | 'campaign';
  recipients: string[]; enabled: boolean; last_period: string | null; last_sent_at: string | null;
}
interface User { id: string; name: string; email: string }

const GROUP = { agent: 'person', department: 'department', queue: 'queue', campaign: 'campaign' } as const;

export function QaReportSchedules({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const mayWrite = can('qa:write');
  const qc = useQueryClient();
  const base = `/v1/tenants/${tenantId}/qa/report-schedules`;
  const list = useQuery({ queryKey: ['qa-schedules', tenantId], queryFn: () => api.get<{ data: Schedule[]; users: User[] }>(base) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['qa-schedules', tenantId] });

  const [editing, setEditing] = useState<Partial<Schedule> & { id?: string } | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (s: Partial<Schedule> & { id?: string }) => {
      const body = { frequency: s.frequency, group_by: s.group_by, recipients: s.recipients, enabled: s.enabled ?? true };
      return s.id ? api.patch(`${base}/${s.id}`, body) : api.post(base, body);
    },
    onSuccess: () => { setEditing(null); refresh(); },
  });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`${base}/${id}`), onSuccess: refresh });
  const toggle = useMutation({ mutationFn: (s: Schedule) => api.patch(`${base}/${s.id}`, { enabled: !s.enabled }), onSuccess: refresh });
  const sendNow = useMutation({
    mutationFn: (id: string) => api.post<{ from: string; to: string; recipients: number }>(`${base}/${id}/send-now`, {}),
    onSuccess: (r) => setNote(`Sent the report for ${r.from} to ${r.to} to ${r.recipients} ${r.recipients === 1 ? 'person' : 'people'}.`),
  });

  const users = list.data?.users ?? [];
  const nameOf = (id: string) => users.find((u) => u.id === id)?.name ?? 'someone no longer on the account';
  const rows = list.data?.data ?? [];

  return (
    <div className="arc-card" style={{ marginTop: 14 }}>
      <div className="arc-card-head">
        <h2 className="arc-h2">By email</h2>
        {mayWrite && !editing && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing({ frequency: 'weekly', group_by: 'agent', recipients: [] })}>
            <Icon name="plus" size={13} /> Schedule
          </button>
        )}
      </div>
      <div className="arc-card-body arc-stack" style={{ gap: 10 }}>
        {list.error && <ErrorBanner error={list.error} />}
        {note && <div className="arc-muted" style={{ fontSize: 12.5 }}>{note}</div>}
        {rows.length === 0 && !editing && (
          <span className="arc-muted" style={{ fontSize: 13 }}>
            The report as a PDF, every Monday for the week before or on the 1st for the month before, to people on the account.
          </span>
        )}
        {rows.map((s) => (
          <div key={s.id} className="arc-row" style={{ gap: 10, flexWrap: 'wrap', opacity: s.enabled ? 1 : 0.6 }}>
            <span style={{ fontWeight: 550 }}>{s.frequency === 'weekly' ? 'Weekly' : 'Monthly'}, by {GROUP[s.group_by]}</span>
            <span className="arc-muted" style={{ fontSize: 12.5, flex: '1 1 240px' }}>to {s.recipients.map(nameOf).join(', ')}</span>
            {!s.enabled && <Badge tone="muted">paused</Badge>}
            {s.last_sent_at && <span className="arc-subtle" style={{ fontSize: 12 }}>last sent {new Date(s.last_sent_at).toLocaleDateString()}</span>}
            {mayWrite && (
              <>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={sendNow.isPending} onClick={() => sendNow.mutate(s.id)}>Send now</button>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => toggle.mutate(s)}>{s.enabled ? 'Pause' : 'Resume'}</button>
                <RowAction kind="edit" label="Edit schedule" onClick={() => setEditing(s)} />
                <RowAction kind="delete" label="Delete schedule" onClick={() => { if (confirm('Stop sending this report?')) remove.mutate(s.id); }} />
              </>
            )}
          </div>
        ))}
        {sendNow.error && <ErrorBanner error={sendNow.error} />}

        {editing && (
          <div className="arc-stack" style={{ gap: 10, padding: 12, border: '1px solid var(--arc-border)', borderRadius: 'var(--arc-r-sm)' }}>
            <div className="arc-row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div className="arc-field" style={{ margin: 0 }}>
                <label className="arc-label" htmlFor="qs-freq">How often</label>
                <select id="qs-freq" className="arc-select" value={editing.frequency} onChange={(e) => setEditing({ ...editing, frequency: e.target.value as Schedule['frequency'] })}>
                  <option value="weekly">Every Monday, for the week before</option>
                  <option value="monthly">On the 1st, for the month before</option>
                </select>
              </div>
              <div className="arc-field" style={{ margin: 0 }}>
                <label className="arc-label" htmlFor="qs-group">Grouped by</label>
                <select id="qs-group" className="arc-select" value={editing.group_by} onChange={(e) => setEditing({ ...editing, group_by: e.target.value as Schedule['group_by'] })}>
                  {Object.entries(GROUP).map(([k, v]) => <option key={k} value={k}>{v[0]!.toUpperCase() + v.slice(1)}</option>)}
                </select>
              </div>
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <span className="arc-label">To</span>
              <div className="arc-row" style={{ gap: 14, flexWrap: 'wrap', marginTop: 4 }}>
                {users.map((u) => (
                  <label key={u.id} className="arc-row" style={{ gap: 6, fontSize: 13 }} title={u.email}>
                    <input type="checkbox" checked={editing.recipients?.includes(u.id) ?? false}
                      onChange={(e) => setEditing({ ...editing, recipients: e.target.checked
                        ? [...(editing.recipients ?? []), u.id] : (editing.recipients ?? []).filter((x) => x !== u.id) })} />
                    {u.name}
                  </label>
                ))}
              </div>
              <div className="arc-hint">People on this account only.</div>
            </div>
            {save.error && <ErrorBanner error={save.error} />}
            <div className="arc-row" style={{ gap: 8 }}>
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!editing.recipients?.length || save.isPending}
                onClick={() => save.mutate(editing)}>{save.isPending ? 'Saving…' : 'Save'}</button>
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
